import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import "./UdhaarManager.css";

// Actual Udhaar Manager app icon from your project
import appLogo from "../../assets/udharmanager.png";

/* =========================================================
   SITE CONFIGURATION
   ========================================================= */

const SITE_URL = "https://www.samsoftix.com";

const PAGE_URL = `${SITE_URL}/apps/udhaar-manager`;

const PLAY_STORE_URL =
    "https://play.google.com/store/apps/details?id=com.samsoftix.udhaarmanager";

const WHATSAPP_URL =
    "https://wa.me/923320472184?text=I%20want%20to%20know%20more%20about%20Udhaar%20Manager";

/*
 * Public website path for the app icon.
 *
 * IMPORTANT:
 * This is used only for structured data.
 * Your visible page logo uses the imported local image above.
 *
 * If /assets/udharmanager.png is NOT publicly available at this URL
 * after deployment, remove "image" from the SoftwareApplication
 * structured data or replace this with the real public image URL.
 */
const APP_LOGO_PUBLIC_URL = `${SITE_URL}/assets/udharmanager.png`;


/* =========================================================
   GOOGLE PLAY SCREENSHOTS
   =========================================================
   Larger CDN dimensions are used to reduce softness caused
   by stretching the original 526px images.
   ========================================================= */

const screenshots = [
    "https://play-lh.googleusercontent.com/yFDkkFeP08DItxIUZK1CSgRe3rrY_e_4jXtUXtdwSJXkjqHRJUkePKryumJV-lwQDJVC-YooopEfKrEQNQVt4A=w1200-h675-rw",

    "https://play-lh.googleusercontent.com/iLDdxRMtCTcL67Ohx6wiMH65lNSxvN68IAj5BrKSRIusuUgReCtYgD_fqBvul2fjIlbUK2s6fApmUAk9myjgYw=w1200-h675-rw",

    "https://play-lh.googleusercontent.com/Mq1QVZEd8xGbfUtJJGmYfYBVJW_esI2EbZ3DCEQSSPNjQ_OrBnHPpmiwKE2Jl9l29UNGZxSWmPatW5nxGBBRUow=w1200-h675-rw",

    "https://play-lh.googleusercontent.com/EBeUdaL-1EisO893aIgNzYUhm_A14IeMslGXVpcmZn3fHR_-goCRqZOxpXr8saCA50mWnk7PNvH61rSkV0hvfQ=w1200-h675-rw",

    "https://play-lh.googleusercontent.com/DZUUWptV03gWMOK2vOwRWe2tPn0v12XMD5-Qa7IwdPHuWf_PAHC6ItsOWSx0y1Sh7S-OpGlaPmx0NhyHytd0=w1200-h675-rw",

    "https://play-lh.googleusercontent.com/usRhOY-c77mg51ZkBQNUnpuqGn8ziLglYsT12h63cBhT2FxnJGSQrytO7-vj6h_AUCMWHF8RH1pWVCLmJHHe=w1200-h675-rw",

    "https://play-lh.googleusercontent.com/QVLR6HnZaFv94_QKgmJlx0Pv2RnyWqa7untUwO3aQpdgHv55prX9CQuwU97ZHKbBol0kkmXH1FctP9fu7CaDyjs=w1200-h675-rw",
];


/* =========================================================
   FEATURES
   ========================================================= */

const features = [
    {
        number: "01",
        icon: "👥",
        title: "Customer Management",
        text:
            "Add and manage customers with names, father names, phone numbers and addresses. Find customers quickly with instant search.",
    },
    {
        number: "02",
        icon: "💰",
        title: "Udhaar & Payments",
        text:
            "Record udhaar, payments received and customer transactions while keeping track of outstanding balances.",
    },
    {
        number: "03",
        icon: "📱",
        title: "WhatsApp Reminders",
        text:
            "Send payment reminders to customers through WhatsApp directly from the app.",
    },
    {
        number: "04",
        icon: "📄",
        title: "PDF Statements",
        text:
            "Generate customer ledger statements and PDF reports that can be shared when needed.",
    },
    {
        number: "05",
        icon: "💾",
        title: "Backup & Restore",
        text:
            "Create local backups and restore your saved data when moving or recovering your device.",
    },
    {
        number: "06",
        icon: "📊",
        title: "Dashboard",
        text:
            "View customers, due accounts and outstanding balances from a simple dashboard.",
    },
];


/* =========================================================
   BUSINESS TYPES
   ========================================================= */

const businessTypes = [
    "Kiryana Stores",
    "Mobile Shops",
    "Pharmacies",
    "Hardware Stores",
    "General Retail",
    "Small Traders",
];


/* =========================================================
   FAQ
   ========================================================= */

const faqs = [
    {
        question: "What is Udhaar Manager?",
        answer:
            "Udhaar Manager is an offline digital khata application designed for managing customers, udhaar records, payments and outstanding balances.",
    },
    {
        question: "Does Udhaar Manager require internet?",
        answer:
            "The core customer and udhaar management features are designed to work offline. Customer data is stored locally on the device.",
    },
    {
        question: "Can I record customer payments?",
        answer:
            "Yes. You can record udhaar entries, payments received and view transaction history for individual customers.",
    },
    {
        question: "Can I send payment reminders on WhatsApp?",
        answer:
            "Yes. Udhaar Manager provides WhatsApp reminder functionality for customers with outstanding balances.",
    },
    {
        question: "Can I generate customer statements?",
        answer:
            "Yes. The app can generate PDF customer statements and reports for sharing or record keeping.",
    },
    {
        question: "Does the app use cloud storage?",
        answer:
            "The app is designed around local data storage rather than cloud storage for customer records.",
    },
    {
        question: "Who is Udhaar Manager designed for?",
        answer:
            "It is designed for Pakistani shopkeepers, small retailers, traders and other businesses that maintain customer credit or udhaar records.",
    },
];


/* =========================================================
   COMPONENT
   ========================================================= */

const UdhaarManager = () => {
    const navigate = useNavigate();

    /* =======================================================
       STRUCTURED DATA
       ======================================================= */

    const structuredData = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "SoftwareApplication",
                "@id": `${PAGE_URL}#software`,

                name: "Udhaar Manager",

                alternateName: "Udhaar Manager Digital Khata",

                description:
                    "Offline digital khata app for Pakistani shopkeepers and small businesses. Manage customers, udhaar, payments, outstanding balances, PDF statements and WhatsApp reminders.",

                applicationCategory: "BusinessApplication",

                applicationSubCategory: "Digital Khata / Ledger",

                operatingSystem: "Android",

                image: APP_LOGO_PUBLIC_URL,

                screenshot: screenshots,

                featureList: [
                    "Customer management",
                    "Udhaar management",
                    "Payment tracking",
                    "Customer transaction history",
                    "Outstanding balance tracking",
                    "WhatsApp payment reminders",
                    "PDF customer statements",
                    "Local backup and restore",
                    "Offline data management",
                    "Dashboard reports",
                ],

                downloadUrl: PLAY_STORE_URL,

                installUrl: PLAY_STORE_URL,

                url: PAGE_URL,

                creator: {
                    "@id": `${SITE_URL}/#organization`,
                },

                publisher: {
                    "@id": `${SITE_URL}/#organization`,
                },
            },

            /* =================================================
               WEB PAGE
            ================================================= */

            {
                "@type": "WebPage",

                "@id": `${PAGE_URL}#webpage`,

                url: PAGE_URL,

                name:
                    "Udhaar Manager | Offline Digital Khata App for Pakistani Businesses",

                description:
                    "Learn about Udhaar Manager, an offline digital khata app for managing customers, udhaar, payments, balances, PDF statements and WhatsApp reminders.",

                isPartOf: {
                    "@id": `${SITE_URL}/#website`,
                },

                about: {
                    "@id": `${PAGE_URL}#software`,
                },

                primaryImageOfPage: {
                    "@type": "ImageObject",
                    contentUrl: screenshots[0],
                },
            },

            /* =================================================
               BREADCRUMBS
            ================================================= */

            {
                "@type": "BreadcrumbList",

                "@id": `${PAGE_URL}#breadcrumb`,

                itemListElement: [
                    {
                        "@type": "ListItem",
                        position: 1,
                        name: "Home",
                        item: SITE_URL,
                    },

                    {
                        "@type": "ListItem",
                        position: 2,
                        name: "Apps",
                        item: `${SITE_URL}/apps`,
                    },

                    {
                        "@type": "ListItem",
                        position: 3,
                        name: "Udhaar Manager",
                        item: PAGE_URL,
                    },
                ],
            },

            /* =================================================
               FAQ
            ================================================= */

            {
                "@type": "FAQPage",

                "@id": `${PAGE_URL}#faq`,

                mainEntity: faqs.map((faq) => ({
                    "@type": "Question",

                    name: faq.question,

                    acceptedAnswer: {
                        "@type": "Answer",
                        text: faq.answer,
                    },
                })),
            },

            /* =================================================
               ORGANIZATION
            ================================================= */

            {
                "@type": "Organization",

                "@id": `${SITE_URL}/#organization`,

                name: "SamSoftix",

                url: SITE_URL,

                email: "info@samsoftix.com",

                telephone: "+92-332-0472184",
            },

            /* =================================================
               WEBSITE
            ================================================= */

            {
                "@type": "WebSite",

                "@id": `${SITE_URL}/#website`,

                url: SITE_URL,

                name: "SamSoftix",

                publisher: {
                    "@id": `${SITE_URL}/#organization`,
                },
            },
        ],
    };


    /* =======================================================
       RENDER
       ======================================================= */

    return (
        <div className="udhaar-page">

            {/* =================================================
                SEO
            ================================================= */}

            <Helmet>

                <title>
                    Udhaar Manager | Digital Khata App for Pakistan
                </title>

                <meta
                    name="description"
                    content="Download Udhaar Manager, an offline digital khata app for Pakistani shopkeepers. Track udhaar, payments, customer balances, PDF statements and WhatsApp reminders."
                />

                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large"
                />

                <meta
                    name="author"
                    content="SamSoftix"
                />

                <meta
                    name="application-name"
                    content="Udhaar Manager"
                />

                <meta
                    name="theme-color"
                    content="#020617"
                />

                <link
                    rel="canonical"
                    href={PAGE_URL}
                />

                {/* =========================
                    Open Graph
                ========================= */}

                <meta
                    property="og:type"
                    content="website"
                />

                <meta
                    property="og:locale"
                    content="en_PK"
                />

                <meta
                    property="og:title"
                    content="Udhaar Manager | Digital Khata App for Pakistan"
                />

                <meta
                    property="og:description"
                    content="Manage udhaar, customers, payments and outstanding balances with Udhaar Manager, an offline digital khata app for Pakistani businesses."
                />

                <meta
                    property="og:url"
                    content={PAGE_URL}
                />

                <meta
                    property="og:site_name"
                    content="SamSoftix"
                />

                <meta
                    property="og:image"
                    content={screenshots[0]}
                />

                <meta
                    property="og:image:width"
                    content="1200"
                />

                <meta
                    property="og:image:height"
                    content="675"
                />

                <meta
                    property="og:image:alt"
                    content="Udhaar Manager digital khata app"
                />

                {/* =========================
                    Twitter / X
                ========================= */}

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content="Udhaar Manager | Digital Khata App"
                />

                <meta
                    name="twitter:description"
                    content="Offline digital khata app for managing customers, udhaar, payments and outstanding balances."
                />

                <meta
                    name="twitter:image"
                    content={screenshots[0]}
                />

                <meta
                    name="twitter:image:alt"
                    content="Udhaar Manager app screenshot"
                />

                {/* =========================
                    Image CDN
                ========================= */}

                <link
                    rel="preconnect"
                    href="https://play-lh.googleusercontent.com"
                />

                {/* =========================
                    Structured Data
                ========================= */}

                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>

            </Helmet>


            {/* =================================================
                HERO
            ================================================= */}

            <header className="udhaar-hero">

                <div className="udhaar-hero-glow" />

                <div className="udhaar-container">

                    {/* Breadcrumb */}

                    <nav
                        className="udhaar-breadcrumb"
                        aria-label="Breadcrumb"
                    >

                        <button
                            type="button"
                            onClick={() => navigate("/")}
                        >
                            Home
                        </button>

                        <span>/</span>

                        <span>Apps</span>

                        <span>/</span>

                        <span>Udhaar Manager</span>

                    </nav>


                    <div className="udhaar-hero-grid">

                        {/* =================================================
                            HERO CONTENT
                        ================================================= */}

                        <div className="udhaar-hero-content">

                            <div className="udhaar-badge">
                                <span>●</span>
                                Offline Digital Khata
                            </div>


                            {/* ACTUAL APP LOGO */}

                            <div className="udhaar-app-icon">

                                <img
                                    src={appLogo}
                                    alt="Udhaar Manager app logo"
                                    className="udhaar-app-logo"
                                    width="512"
                                    height="512"
                                    loading="eager"
                                    fetchPriority="high"
                                    decoding="async"
                                />

                            </div>


                            <h1>
                                Udhaar Manager

                                <span>
                                    Digital Khata for Pakistani Businesses
                                </span>
                            </h1>


                            <p className="udhaar-hero-subtitle">
                                Simple offline khata management for
                                shopkeepers and small businesses.
                            </p>


                            <p className="udhaar-hero-description">
                                Manage customers, udhaar records,
                                payments, outstanding balances,
                                PDF statements and WhatsApp reminders
                                without depending on cloud storage.
                            </p>


                            {/* ACTIONS */}

                            <div className="udhaar-hero-actions">

                                <a
                                    href={PLAY_STORE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="udhaar-primary-btn"
                                    aria-label="Download Udhaar Manager from Google Play"
                                >

                                    <span>▶</span>

                                    <span>
                                        <small>Available on</small>
                                        Google Play
                                    </span>

                                </a>


                                <a
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="udhaar-whatsapp-btn"
                                >
                                    WhatsApp
                                </a>


                                <button
                                    type="button"
                                    className="udhaar-secondary-btn"
                                    onClick={() => navigate("/contact")}
                                >
                                    Build Your App
                                </button>

                            </div>


                            {/* APP META */}

                            <div className="udhaar-hero-meta">

                                <div>
                                    <strong>10+</strong>
                                    <span>Downloads</span>
                                </div>

                                <div>
                                    <strong>Android</strong>
                                    <span>Platform</span>
                                </div>

                                <div>
                                    <strong>3+</strong>
                                    <span>Content Rating</span>
                                </div>

                                <div>
                                    <strong>Sep 2026</strong>
                                    <span>Updated</span>
                                </div>

                            </div>

                        </div>


                        {/* =================================================
                            HERO SCREENSHOT
                        ================================================= */}

                        <div className="udhaar-hero-preview">

                            <div className="udhaar-phone">

                                <div className="udhaar-phone-top">
                                    <span />
                                    <span />
                                    <span />
                                </div>

                                <img
                                    src={screenshots[0]}
                                    alt="Udhaar Manager digital khata app dashboard showing customer and udhaar management"
                                    width="1200"
                                    height="675"
                                    loading="eager"
                                    fetchPriority="high"
                                    decoding="async"
                                />

                            </div>

                        </div>

                    </div>

                </div>

            </header>


            {/* =================================================
                MAIN CONTENT
            ================================================= */}

            <main className="udhaar-main">


                {/* =================================================
                    ABOUT
                ================================================= */}

                <section
                    className="udhaar-section udhaar-about"
                    id="about"
                >

                    <div className="udhaar-container">

                        <div className="udhaar-section-heading">

                            <span>ABOUT THE APP</span>

                            <h2>
                                A simple digital khata
                                <strong>
                                    {" "}built for everyday business.
                                </strong>
                            </h2>

                        </div>


                        <div className="udhaar-about-grid">

                            <div className="udhaar-about-text">

                                <p>
                                    Udhaar Manager is an offline digital
                                    khata application designed for
                                    Pakistani shopkeepers and small
                                    businesses.
                                </p>

                                <p>
                                    Instead of maintaining customer credit
                                    records in a physical notebook, you can
                                    organize customers, udhaar transactions,
                                    payments and balances directly on your
                                    Android device.
                                </p>

                                <p>
                                    The application focuses on a simple
                                    workflow so that everyday shop
                                    transactions can be recorded quickly.
                                </p>

                            </div>


                            <div className="udhaar-about-card">

                                <div>
                                    <span>01</span>

                                    <strong>
                                        Offline First
                                    </strong>

                                    <p>
                                        Core data management is designed
                                        around local device storage.
                                    </p>
                                </div>


                                <div>
                                    <span>02</span>

                                    <strong>
                                        Simple Workflow
                                    </strong>

                                    <p>
                                        Add customers, record transactions
                                        and check balances quickly.
                                    </p>
                                </div>


                                <div>
                                    <span>03</span>

                                    <strong>
                                        Business Focused
                                    </strong>

                                    <p>
                                        Built around the needs of small
                                        shops and retail businesses.
                                    </p>
                                </div>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =================================================
                    FEATURES
                ================================================= */}

                <section
                    className="udhaar-section"
                    id="features"
                >

                    <div className="udhaar-container">

                        <div className="udhaar-section-heading">

                            <span>FEATURES</span>

                            <h2>
                                Everything you need to
                                <strong>
                                    {" "}manage your khata.
                                </strong>
                            </h2>

                            <p>
                                Keep everyday customer credit and payment
                                records organized in one place.
                            </p>

                        </div>


                        <div className="udhaar-feature-grid">

                            {features.map((feature) => (

                                <article
                                    className="udhaar-feature-card"
                                    key={feature.number}
                                >

                                    <div className="udhaar-feature-number">
                                        {feature.number}
                                    </div>

                                    <div className="udhaar-feature-icon">
                                        {feature.icon}
                                    </div>

                                    <h3>
                                        {feature.title}
                                    </h3>

                                    <p>
                                        {feature.text}
                                    </p>

                                </article>

                            ))}

                        </div>

                    </div>

                </section>


                {/* =================================================
                    SCREENSHOTS
                ================================================= */}

                <section
                    className="udhaar-section"
                    id="screenshots"
                >

                    <div className="udhaar-container">

                        <div className="udhaar-section-heading">

                            <span>APP SCREENSHOTS</span>

                            <h2>
                                See Udhaar Manager
                                <strong>
                                    {" "}in action.
                                </strong>
                            </h2>

                            <p>
                                Explore the interface for customers,
                                transactions, balances and reports.
                            </p>

                        </div>


                        <div className="udhaar-screenshot-grid">

                            {screenshots.map((image, index) => (

                                <article
                                    className="udhaar-screenshot-card"
                                    key={image}
                                >

                                    <img
                                        src={image}
                                        alt={`Udhaar Manager app screenshot ${index + 1}`}
                                        width="1200"
                                        height="675"
                                        loading="lazy"
                                        decoding="async"
                                        sizes="(max-width: 760px) 100vw, (max-width: 1050px) 50vw, 33vw"
                                    />

                                    <div className="udhaar-screenshot-overlay">
                                        Screenshot {index + 1}
                                    </div>

                                </article>

                            ))}

                        </div>

                    </div>

                </section>


                {/* =================================================
                    WORKFLOW
                ================================================= */}

                <section
                    className="udhaar-section"
                    id="workflow"
                >

                    <div className="udhaar-container">

                        <div className="udhaar-section-heading">

                            <span>SIMPLE WORKFLOW</span>

                            <h2>
                                Manage customer credit
                                <strong>
                                    {" "}in a few steps.
                                </strong>
                            </h2>

                        </div>


                        <div className="udhaar-workflow">

                            <div className="udhaar-workflow-step">

                                <span>01</span>

                                <strong>
                                    Add Customer
                                </strong>

                                <p>
                                    Save customer details for
                                    quick access.
                                </p>

                            </div>


                            <div className="udhaar-workflow-line" />


                            <div className="udhaar-workflow-step">

                                <span>02</span>

                                <strong>
                                    Record Udhaar
                                </strong>

                                <p>
                                    Add credit transactions and
                                    keep the ledger updated.
                                </p>

                            </div>


                            <div className="udhaar-workflow-line" />


                            <div className="udhaar-workflow-step">

                                <span>03</span>

                                <strong>
                                    Record Payment
                                </strong>

                                <p>
                                    Record payments and track
                                    the remaining balance.
                                </p>

                            </div>


                            <div className="udhaar-workflow-line" />


                            <div className="udhaar-workflow-step">

                                <span>04</span>

                                <strong>
                                    Share Statement
                                </strong>

                                <p>
                                    Generate a PDF statement or
                                    send a reminder through WhatsApp.
                                </p>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =================================================
                    BUSINESS TYPES
                ================================================= */}

                <section
                    className="udhaar-section"
                    id="businesses"
                >

                    <div className="udhaar-container">

                        <div className="udhaar-section-heading">

                            <span>WHO CAN USE IT?</span>

                            <h2>
                                Designed for
                                <strong>
                                    {" "}small businesses.
                                </strong>
                            </h2>

                            <p>
                                Useful for businesses that manage
                                customer credit or udhaar records.
                            </p>

                        </div>


                        <div className="udhaar-business-grid">

                            {businessTypes.map((business, index) => (

                                <div
                                    className="udhaar-business-card"
                                    key={business}
                                >

                                    <span>
                                        {String(index + 1).padStart(2, "0")}
                                    </span>

                                    <strong>
                                        {business}
                                    </strong>

                                </div>

                            ))}

                        </div>

                    </div>

                </section>


                {/* =================================================
                    PRIVACY
                ================================================= */}

                <section
                    className="udhaar-section"
                    id="privacy"
                >

                    <div className="udhaar-container">

                        <div className="udhaar-privacy">

                            <div className="udhaar-privacy-grid">

                                <div className="udhaar-privacy-main">

                                    <span>
                                        DATA & PRIVACY
                                    </span>

                                    <h2>
                                        Your khata stays
                                        <strong>
                                            {" "}on your device.
                                        </strong>
                                    </h2>

                                    <p>
                                        Udhaar Manager is designed as an
                                        offline-first digital khata application.
                                        Customer records are stored locally
                                        rather than relying on cloud storage
                                        for the core ledger.
                                    </p>

                                </div>


                                <div className="udhaar-privacy-note">

                                    <div className="udhaar-privacy-points">

                                        <div>
                                            <span>✓</span>

                                            <strong>
                                                No cloud storage for core records
                                            </strong>
                                        </div>


                                        <div>
                                            <span>✓</span>

                                            <strong>
                                                Works without continuous internet
                                            </strong>
                                        </div>


                                        <div>
                                            <span>✓</span>

                                            <strong>
                                                Local backup and restore
                                            </strong>
                                        </div>


                                        <div>
                                            <span>✓</span>

                                            <strong>
                                                Simple local data management
                                            </strong>
                                        </div>

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =================================================
                    APP DETAILS
                ================================================= */}

                <section
                    className="udhaar-section"
                    id="details"
                >

                    <div className="udhaar-container">

                        <div className="udhaar-section-heading">

                            <span>APP DETAILS</span>

                            <h2>
                                Udhaar Manager
                                <strong>
                                    {" "}at a glance.
                                </strong>
                            </h2>

                        </div>


                        <div className="udhaar-details-grid">

                            <div>
                                <span>App Name</span>
                                <strong>Udhaar Manager</strong>
                            </div>


                            <div>
                                <span>Developer</span>
                                <strong>Qasim Mehmood Cheema</strong>
                            </div>


                            <div>
                                <span>Platform</span>
                                <strong>Android</strong>
                            </div>


                            <div>
                                <span>Content Rating</span>
                                <strong>Rated 3+</strong>
                            </div>


                            <div>
                                <span>Downloads</span>
                                <strong>20+</strong>
                            </div>


                            <div>
                                <span>Last Updated</span>
                                <strong>September 19, 2026</strong>
                            </div>

                        </div>

                    </div>

                </section>


                {/* =================================================
                    FAQ
                ================================================= */}

                <section
                    className="udhaar-section"
                    id="faq"
                >

                    <div className="udhaar-container">

                        <div className="udhaar-section-heading">

                            <span>FAQ</span>

                            <h2>
                                Frequently asked
                                <strong>
                                    {" "}questions.
                                </strong>
                            </h2>

                        </div>


                        <div className="udhaar-faq-list">

                            {faqs.map((faq, index) => (

                                <details
                                    className="udhaar-faq-item"
                                    key={faq.question}
                                    open={index === 0}
                                >

                                    <summary>

                                        <span>
                                            {faq.question}
                                        </span>

                                        <b>
                                            +
                                        </b>

                                    </summary>

                                    <p>
                                        {faq.answer}
                                    </p>

                                </details>

                            ))}

                        </div>

                    </div>

                </section>


                {/* =================================================
                    DOWNLOAD CTA
                ================================================= */}

                <section className="udhaar-section">

                    <div className="udhaar-container">

                        <div className="udhaar-cta">

                            <div className="udhaar-cta-glow" />

                            <span className="udhaar-cta-label">
                                GET UDHAAR MANAGER
                            </span>

                            <h2>
                                Ready to move your
                                <strong>
                                    {" "}khata to digital?
                                </strong>
                            </h2>

                            <p>
                                Download Udhaar Manager from Google Play
                                and organize your customer credit records
                                digitally.
                            </p>


                            <div className="udhaar-cta-actions">

                                <a
                                    href={PLAY_STORE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="udhaar-primary-btn"
                                    aria-label="Download Udhaar Manager from Google Play"
                                >

                                    <span>▶</span>

                                    <span>
                                        <small>Download on</small>
                                        Google Play
                                    </span>

                                </a>


                                <a
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="udhaar-whatsapp-btn"
                                >
                                    Ask on WhatsApp
                                </a>

                            </div>


                            <div className="udhaar-cta-location">
                                Developed by SamSoftix
                            </div>

                        </div>

                    </div>

                </section>

            </main>

        </div>
    );
};


export default UdhaarManager;